/**
 * S21a. The one time claim link. `/claim/[token]`
 *
 * One of the two V1 acceptance tests, and the emotional beat is CONTINUITY.
 * Not "congratulations", not "welcome aboard", not a migration wizard.
 * Nothing moved. The app this person built is already on the internet, it has
 * been on the internet for weeks, and the only thing about to change is whose
 * name is on it.
 *
 * THE PREVIEW CARD NEVER LEAVES THE SCREEN, and that is the whole design.
 * The project, its web address, its Live pill and its one deploy are on screen
 * before there is an account, they stay on screen while the account is being
 * made, and they stay on screen while the transfer runs underneath them. A
 * flow that swapped the card out for a form would be telling the person their
 * app went somewhere, at the exact moment the product's argument is that it
 * did not. Seeing the thing already alive, before you have signed up for
 * anything, is the entire persuasion.
 *
 * THE WEB ADDRESS IS PRINTED UNCHANGED, in the copy field, at the largest
 * size the atom has, in three of the six states. It is the same literal on the
 * welcome screen and the same literal on the overview afterwards. Nobody is
 * ever shown an old address and a new one, because there is only one.
 *
 * THE WORD "LOOP" APPEARS EXACTLY ONCE on this screen and nowhere else in the
 * product, in the card's eyebrow, as the place the project is arriving from.
 * That is the single exception the vocabulary contract carves out, and it is
 * spent here on purpose: it answers "is this the same thing I made?" with a
 * proper noun in one line, which is cheaper than a paragraph. The account
 * copy below it deliberately does not spend it a second time.
 *
 * DEVIATION FROM SCREEN SPEC S21a, recorded. The spec puts the beacon on
 * `Continue with GitHub` and makes email the secondary. The ink block here is
 * on the email submit instead, and GitHub is the outline. The reason is the
 * separate account decision: this is the moment a person learns that the
 * account they are about to have is a new one, and an email field is where
 * that lands. A person arriving on a claim link has no SV Cloud account by
 * definition, and half of them built their project without ever touching a
 * GitHub login of their own. Both controls are present and neither is hidden.
 *
 * THE DECLINED PATH STATES THE CONSEQUENCE AS A DATE, not as "soon" and not
 * as a warning triangle with no number in it. If nobody claims the project the
 * cleanup removes it, and a person deciding to walk away is entitled to know
 * exactly what they are walking away from and what they are keeping. Their
 * code stays in GitHub either way, and that sentence sits in the same block as
 * the loss rather than in a footnote, because splitting them is how a screen
 * becomes a scare screen.
 *
 * THE TRANSFER FAILURE HAS NO RETRY BUTTON. The person cannot fix a tenant
 * flip that stalled halfway, so offering them a button that pretends they can
 * would be a lie told at the worst possible moment. The screen says the
 * project is safe, says it is still answering at its address, says we know,
 * and then gets out of the way.
 *
 * States, every one deep linkable per prototype mechanic (a):
 *   /claim/9f2c-8d41-7a06                        the valid link, Sam's scenario
 *   /claim/claim_7fd2                            the valid link, Graduate
 *   ?state=accept                                the accept step
 *   ?state=moving                                the narrated transfer
 *   ?state=transfer-failed                       stalled midway, no retry
 *   ?state=claimed                               this link has been used
 *   ?state=expired                               the window closed
 *   ?state=declined                              said no, with the consequence
 * A token that names no claim renders its own state rather than a blank page.
 *
 * The narration is clock derived, exactly like every other asynchronous thing
 * in the prototype: nothing here accumulates, every value is a pure function
 * of (now - startedAt) * speed, so a refresh mid transfer resumes correctly
 * and a backgrounded tab comes back right instead of behind. It schedules its
 * own repaints through useWallClock because the transfer has no entry in
 * scripts.ts, which is the same arrangement S19's cleaning up narration uses.
 */

import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';

import { Button } from '../../components/primitives/Button';
import { Field } from '../../components/primitives/Field';

import { StepLedger } from '../../components/patterns/AsyncNarration';
import type { NarrationStep } from '../../components/patterns/AsyncNarration';
import { ConsequenceList } from '../../components/patterns/ConsequenceList';
import type { ConsequenceItem } from '../../components/patterns/ConsequenceList';
import { CopyField } from '../../components/patterns/CopyField';
import { dateLabel, whenLabel } from '../../components/patterns/DeployRow';
import { HeroBackdrop } from '../../components/patterns/HeroBackdrop';
import { Notice } from '../../components/patterns/Notice';
import { StatusPill } from '../../components/patterns/StatusPill';
import { VisitLink } from '../../components/patterns/VisitLink';
import { GitHubMark } from '../../components/brand/GitHubMark';
import { Wordmark } from '../../components/brand/Wordmark';

import { getClaim, getProject, listDeploys } from '../../lib/api';
import { durationSeconds, shortSha } from '../../lib/format';
import { useAsync } from '../../lib/hooks/use-async';
import { useScreenState } from '../../lib/hooks/use-screen-state';
import { useWallClock } from '../../lib/hooks/use-wall-clock';
import { t } from '../../lib/i18n/t';
import { CLEANUP_GRACE_DAYS } from '../../lib/mock/seed';
import { useDemoStore } from '../../lib/mock/store';
import { captureEvent } from '../../lib/analytics/posthog';
import type { Speed, StepStatus } from '../../lib/types';

import '../../styles/screens/graduation.css';

const STATES = [
  'account',
  'accept',
  'moving',
  'transfer-failed',
  'claimed',
  'expired',
  'declined',
] as const;
type Phase = (typeof STATES)[number];

/**
 * The transfer, three steps and six seconds.
 *
 * It is short because the honest truth is that this operation is short. A
 * tenant flip moves a row, swaps a plan and drops a scheduled deletion, and
 * none of that touches a byte of the person's data. Padding it out to feel
 * momentous would be theatre, and the screen would be lying about the one
 * thing it is here to prove: that nothing is being moved anywhere.
 *
 * The three labels are the three facts the welcome screen then puts in its
 * ledger, in the same order and the same words, so the narration and the
 * receipt are the same list read twice.
 */
const TRANSFER_STEPS: { label: string; ms: number }[] = [
  { label: 'Moving ownership', ms: 2_500 },
  { label: 'Switching your plan', ms: 2_000 },
  { label: 'Clearing the cleanup date', ms: 1_500 },
];

const TRANSFER_TOTAL = TRANSFER_STEPS.reduce((sum, step) => sum + step.ms, 0);

/** The step a stalled transfer stops on: the plan swap, halfway through. */
const FAIL_AT = 1;

/** Clock derived, never accumulated. Instant lands the whole run at once. */
function elapsedOf(startedAt: number, now: number, speed: Speed): number {
  if (speed === 'instant') return TRANSFER_TOTAL;
  return Math.min(TRANSFER_TOTAL, Math.max(0, (now - startedAt) * speed));
}

function transferLedger(elapsed: number, failedIndex = -1): NarrationStep[] {
  let start = 0;
  return TRANSFER_STEPS.map((step, index) => {
    const end = start + step.ms;

    let status: StepStatus;
    if (failedIndex >= 0) {
      // A stalled transfer keeps its finished steps on screen. "Ownership
      // moved and then the plan swap stopped" is a materially different
      // sentence from "it failed", and the ledger has to be able to prove it.
      status = index < failedIndex ? 'succeeded' : index === failedIndex ? 'failed' : 'skipped';
    } else {
      status = elapsed >= end ? 'succeeded' : elapsed > start ? 'running' : 'pending';
    }

    const shown =
      status === 'succeeded' || status === 'failed'
        ? durationSeconds(step.ms)
        : status === 'running'
          ? durationSeconds(elapsed - start)
          : undefined;

    start = end;
    return { label: t(step.label), status, elapsed: shown };
  });
}

const DAY = 24 * 60 * 60 * 1000;

/* ------------------------------------------------------------------ screen */

export default function Claim() {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const scenarioId = useDemoStore((s) => s.scenarioId);
  const speed = useDemoStore((s) => s.prefs.speed);
  const { state, setState } = useScreenState<Phase>(STATES, 'account');

  const [email, setEmail] = useState('');
  const [movingAt, setMovingAt] = useState<number | null>(null);

  /**
   * The ink block waits for an address rather than letting the browser scold.
   * Native constraint validation would fire an unstyled bubble in the host
   * language, which is the one piece of chrome the design system cannot reach
   * and t() cannot translate. Waitlist, NameApp and ConnectCode all gate their
   * ink block the same way: flat until there is something to press it with.
   */
  const ready = email.trim().length > 0;

  /**
   * True only when the transfer was started by pressing the button here.
   *
   * ?state=moving exists to be looked at, so a deep link holds the narration
   * still at its ending instead of walking off to the welcome screen while a
   * reviewer is reading it. Only a real press routes.
   */
  const pressed = useRef(false);

  const { data, isLoading } = useAsync(async () => {
    const claim = await getClaim(token);
    if (!claim) return null;
    const project = await getProject(claim.projectSlug);
    const deploys = project ? await listDeploys(project.id) : [];
    return { claim, project, deploys };
  }, [token, scenarioId]);

  useEffect(() => {
    setMovingAt(state === 'moving' ? Date.now() : null);
  }, [state]);

  const clock = useWallClock(movingAt !== null);
  const elapsed = movingAt === null ? 0 : elapsedOf(movingAt, clock, speed);
  const finished = movingAt !== null && elapsed >= TRANSFER_TOTAL;

  const slug = data?.claim.projectSlug ?? '';

  useEffect(() => {
    if (!finished || !pressed.current || !slug) return;
    navigate(`/welcome/${encodeURIComponent(slug)}`);
  }, [finished, slug, navigate]);

  useEffect(() => {
    if (data?.claim) {
      const { claim } = data;
      const phase: Phase =
        state === 'account' && claim.state !== 'pending'
          ? claim.state === 'expired'
            ? 'expired'
            : 'claimed'
          : state;
      captureEvent('claim_screen_viewed', {
        claim_token: token,
        claim_state: claim.state,
        phase,
        project_name: claim.projectName,
      });
    }
  }, [data, token, state]);

  if (isLoading) {
    return (
      <div className="grad">
        <HeroBackdrop reach="tight" />
        <div className="grad__lockup">
          <Wordmark size={32} />
        </div>
        <p className="grad__say" role="status" aria-live="polite">
          {t('Looking up your project')}
        </p>
      </div>
    );
  }

  // A token that names nothing is its own state, and it is a real one: these
  // links are pasted out of email and get truncated on the way. Saying which
  // of the two things went wrong is not possible, so the screen says neither
  // and offers the two ways forward instead.
  if (!data) {
    return (
      <div className="grad">
        <HeroBackdrop reach="tight" />
        <div className="grad__lockup">
          <Wordmark size={32} />
        </div>
        <Notice tone="quiet" title={t("This link isn't a claim link.")}>
          <p>
            {t(
              'It may have been cut short when it was copied. Open the link from your email again, and if that does not work, ask for a new one.',
            )}
          </p>
        </Notice>
        <p className="grad__foot">
          <Link className="link-inline" to="/sign-in">
            {t('Sign in instead')}
          </Link>
        </p>
      </div>
    );
  }

  const { claim, project, deploys } = data;
  const address = claim.webAddress;
  const host = address.replace(/^https?:\/\//, '');
  const expires = dateLabel(claim.expiresAt);

  // The removal date is data, not arithmetic, wherever the record carries it.
  // The fallback states the grace period the policy actually promises rather
  // than printing a guess as though it came from us.
  const cleanup = dateLabel(
    claim.cleanupAt ??
      new Date(new Date(claim.expiresAt).getTime() + CLEANUP_GRACE_DAYS * DAY).toISOString(),
  );

  const latest = deploys.find((one) => one.isCurrent) ?? deploys[0];

  const phase: Phase =
    state === 'account' && claim.state !== 'pending'
      ? claim.state === 'expired'
        ? 'expired'
        : 'claimed'
      : state;

  const keeps: ConsequenceItem[] = [
    {
      kind: 'survives',
      text: t('{host} keeps working. It is the same address, on the same day.', { host }),
    },
    { kind: 'survives', text: t('Your database comes with it, and everything in it.') },
    { kind: 'survives', text: t('Your files come with it.') },
    { kind: 'survives', text: t('Your keys come with it, and your deploy history.') },
    { kind: 'survives', text: t("Your code stays in GitHub. We don't touch your repository.") },
  ];

  /* --------------------------------------------------------- the project */

  const card = (
    <section className="gcard" aria-label={t('Your project')}>
      <p className="gcard__from mono">{t('Arriving from {from}', { from: claim.arrivingFrom })}</p>

      <div className="gcard__head">
        <h2 className="gcard__name">{claim.projectName}</h2>
        <StatusPill value={project?.status === 'paused' ? 'paused' : 'live'} />
      </div>

      {/* The host, not the full URL, and the reason is that this literal has
          to be recognisable at a glance rather than complete. It is the string
          the person has been typing into their phone for weeks, it is the
          string screen spec S21c prints, and dropping the scheme is what keeps
          it whole at the largest size the copy field has, on a phone, in Thai.
          A web address that ellipsises on the screen whose entire argument is
          that the web address did not change would be an own goal. */}
      <CopyField label={t('Web address')} value={host} size="lg" />

      <div className="gcard__foot">
        {latest ? (
          <p className="gcard__deploy">
            <span className="mono">{shortSha(latest.commitSha)}</span>{' '}
            <span className="gcard__msg">{latest.commitMessage}</span>{' '}
            <span className="gcard__when">{whenLabel(latest.createdAt)}</span>
          </p>
        ) : null}
        <VisitLink href={address} label={t('Visit')} />
      </div>
    </section>
  );

  /* ------------------------------------------------------------ the steps */

  const accountStep = (
    <>
      <header className="grad__head">
        <h1 className="grad__title">
          {t('{project} is ready to be yours', { project: claim.projectName })}
        </h1>
        <p className="grad__say">
          {t('This is {project}, the app you built. It stays exactly where it is.', {
            project: claim.projectName,
          })}
        </p>
        <p className="grad__say grad__say--quiet">
          {t(
            'Its web address, its database, its files and its code all stay exactly as they are. What changes is the name on it.',
          )}
        </p>
      </header>

      {card}

      <form
        className="grad__form"
        onSubmit={(event) => {
          event.preventDefault();
          setState('accept');
        }}
      >
        <h2 className="grad__step">{t('Create your SV Cloud account')}</h2>
        <p className="grad__say grad__say--quiet">
          {t(
            'This is a new account and it belongs to you. It does not have to use the email address you built the project with.',
          )}
        </p>

        <Field
          label={t('Email address')}
          type="email"
          name="email"
          required
          autoComplete="email"
          placeholder="you@example.com"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />

        <div className="grad__wide">
          <Button variant="solid" type="submit" disabled={!ready}>
            {t('Create my account')}
          </Button>
        </div>

        <div className="grad__or">
          <span className="grad__or-word">{t('or')}</span>
        </div>

        <div className="grad__wide">
          <Button variant="outline" onClick={() => setState('accept')}>
            <span className="grad__gh-label">
              <GitHubMark />
              {t('Continue with GitHub')}
            </span>
          </Button>
        </div>
      </form>

      <p className="grad__foot">{t('This link expires on {date}.', { date: expires })}</p>
      <p className="grad__foot">
        <button type="button" className="grad__quiet" onClick={() => setState('declined')}>
          {t("I don't want this project")}
        </button>
      </p>
    </>
  );

  const acceptStep = (
    <>
      <header className="grad__head">
        <h1 className="grad__title">
          {t('Move {project} to your account', { project: claim.projectName })}
        </h1>
        <p className="grad__say">
          {t('Nothing about your app changes. It does not go offline, even for a second.')}
        </p>
      </header>

      {card}

      <section className="grad__block" aria-label={t('What comes with it')}>
        <h2 className="grad__step">{t('What comes with it')}</h2>
        <ConsequenceList items={keeps} label={t('What comes with it')} />
        <p className="grad__say grad__say--quiet">
          {t(
            'Your plan sets the limits on the project. Everything else is the same.',
          )}
        </p>
      </section>

      <div className="grad__wide">
        <Button
          variant="solid"
          onClick={() => {
            pressed.current = true;
            setState('moving');
          }}
        >
          {t('Move it to my account')}
        </Button>
      </div>

      <p className="grad__foot">
        <button type="button" className="grad__quiet" onClick={() => setState('account')}>
          {t('Go back')}
        </button>
      </p>
    </>
  );

  const movingStep = (
    <>
      <header className="grad__head">
        <h1 className="grad__title">
          {t('Moving {project} to your account', { project: claim.projectName })}
        </h1>
        <p className="grad__say">{t('This takes a few seconds. Your app stays up throughout.')}</p>
      </header>

      {card}

      <div className="grad__block">
        <StepLedger steps={transferLedger(elapsed)} />
      </div>
    </>
  );

  const failedStep = (
    <>
      <header className="grad__head">
        <h1 className="grad__title">
          {t("We couldn't finish moving {project}", { project: claim.projectName })}
        </h1>
      </header>

      {card}

      <div className="grad__block">
        <StepLedger steps={transferLedger(TRANSFER_TOTAL, FAIL_AT)} />
      </div>

      {/* No retry, on purpose. Nothing on this screen is something the person
          can do, so nothing on this screen asks them to do it. */}
      <Notice tone="attention">
        <p>
          {t('Your project is safe and still running at {host}.', { host })}
        </p>
        <p>{t("We've been told about this and will finish it. You don't need to do anything.")}</p>
      </Notice>
    </>
  );

  const claimedStep = (
    <>
      <header className="grad__head">
        <h1 className="grad__title">{t('This project has already been claimed.')}</h1>
        <p className="grad__say">
          {t('A claim link works once. If it was you, sign in and {project} is there.', {
            project: claim.projectName,
          })}
        </p>
      </header>

      {card}

      <div className="grad__wide">
        <Link to="/sign-in">
          <Button variant="solid">{t('Sign in')}</Button>
        </Link>
      </div>
    </>
  );

  const expiredStep = (
    <>
      <header className="grad__head">
        <h1 className="grad__title">{t('This link expired on {date}.', { date: expires })}</h1>
        <p className="grad__say">
          {t('{project} is still running, and it is still yours to claim.', {
            project: claim.projectName,
          })}
        </p>
      </header>

      {card}

      <Notice tone="quiet">
        <p>
          {t(
            'If nobody claims it, the cleanup removes this project on {date} and {host} stops working.',
            { date: cleanup, host },
          )}
        </p>
        <p>{t('Your code stays in GitHub.')}</p>
      </Notice>

      <div className="grad__wide">
        <Button variant="solid" onClick={() => setState('account')}>
          {t('Ask for a new link')}
        </Button>
      </div>
    </>
  );

  const declinedStep = (
    <>
      <header className="grad__head">
        <h1 className="grad__title">{t('All right, nothing has been moved.')}</h1>
        <p className="grad__say">
          {t('{project} is still running at its address, and this link still works until {date}.', {
            project: claim.projectName,
            date: expires,
          })}
        </p>
      </header>

      {card}

      {/* The loss and what survives it sit in the same block. Splitting them
          is how a plain consequence turns into a scare screen, and the whole
          question a person has at this moment is which of the two applies to
          the work they actually care about. */}
      <Notice tone="attention" title={t('What happens if you do nothing')}>
        <p>
          {t(
            "If you do nothing, the school's cleanup removes this project on {date}. Your code stays in GitHub.",
            { date: cleanup },
          )}
        </p>
      </Notice>

      <div className="grad__wide">
        <Button variant="solid" onClick={() => setState('account')}>
          {t('Actually, I want it')}
        </Button>
      </div>
    </>
  );

  const body =
    phase === 'accept'
      ? acceptStep
      : phase === 'moving'
        ? movingStep
        : phase === 'transfer-failed'
          ? failedStep
          : phase === 'claimed'
            ? claimedStep
            : phase === 'expired'
              ? expiredStep
              : phase === 'declined'
                ? declinedStep
                : accountStep;

  return (
    <div className="grad">
      <HeroBackdrop reach="tight" />

      <div className="grad__lockup">
        <Wordmark size={32} />
      </div>

      {body}
    </div>
  );
}
